import React, { useState, useEffect, ReactNode } from 'react';
import { authAPI } from '../services/api';
import ConfirmModal from './ConfirmModal';
import vozymeTypeLogo from '../assets/vozyme-type.png';
import { NAV_GROUPS, findNavItem } from './navigation';
import './Layout.css';

interface LayoutProps {
  children: ReactNode;
  onLogout: () => void;
  currentTab: string;
  onTabChange: (tab: string) => void;
}

const MOBILE_QUERY = '(max-width: 960px)';

const initials = (first?: string, last?: string): string =>
  `${(first || '').charAt(0)}${(last || '').charAt(0)}`.toUpperCase() || 'A';

const Layout: React.FC<LayoutProps> = ({ children, onLogout, currentTab, onTabChange }) => {
  const [isMobile, setIsMobile] = useState<boolean>(() => window.matchMedia(MOBILE_QUERY).matches);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [showLogoutModal, setShowLogoutModal] = useState<boolean>(false);
  const currentUser = authAPI.getCurrentUser();
  const current = findNavItem(currentTab);

  useEffect(() => {
    const media = window.matchMedia(MOBILE_QUERY);
    const onChange = (): void => {
      setIsMobile(media.matches);
      if (!media.matches) setDrawerOpen(false);
    };
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  const handleNavClick = (tabId: string): void => {
    onTabChange(tabId);
    setDrawerOpen(false);
  };

  const handleLogoutConfirm = (): void => {
    authAPI.logout();
    onLogout();
    setShowLogoutModal(false);
  };

  return (
    <div className={`bo-shell ${isMobile ? 'is-mobile' : ''} ${drawerOpen ? 'drawer-open' : ''}`}>
      {isMobile && drawerOpen && (
        <div className="bo-backdrop" onClick={() => setDrawerOpen(false)} aria-hidden="true" />
      )}

      <aside id="bo-sidebar" className="bo-sidebar" aria-label="Навигация">
        <div className="bo-brand">
          <img src={vozymeTypeLogo} alt="VozyMe" className="bo-brand-logo" />
          <span className="bo-brand-tag">Back office</span>
          {isMobile && (
            <button
              type="button"
              className="bo-icon-btn bo-drawer-close"
              onClick={() => setDrawerOpen(false)}
              aria-label="Затвори менюто"
            >
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>

        <nav className="bo-nav">
          {NAV_GROUPS.map((group) => (
            <div key={group.label} className="bo-nav-group">
              <div className="bo-nav-label">{group.label}</div>
              {group.items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleNavClick(item.id)}
                  className={`bo-nav-item ${currentTab === item.id ? 'active' : ''}`}
                  aria-current={currentTab === item.id ? 'page' : undefined}
                >
                  <i className={item.icon} aria-hidden="true"></i>
                  <span>{item.name}</span>
                </button>
              ))}
            </div>
          ))}
        </nav>

        <div className="bo-sidebar-footer">
          <div className="bo-avatar" aria-hidden="true">
            {initials(currentUser?.first_name, currentUser?.last_name)}
          </div>
          <div className="bo-user">
            <div className="bo-user-name">
              {currentUser?.first_name} {currentUser?.last_name}
            </div>
            <div className="bo-user-role">Администратор</div>
          </div>
          <button
            type="button"
            className="bo-icon-btn"
            onClick={() => setShowLogoutModal(true)}
            title="Изход"
            aria-label="Изход"
          >
            <i className="fas fa-sign-out-alt"></i>
          </button>
        </div>
      </aside>

      <div className="bo-main">
        <header className="bo-topbar">
          {isMobile && (
            <button
              type="button"
              className="bo-icon-btn bo-menu-btn"
              onClick={() => setDrawerOpen(true)}
              aria-label="Отвори менюто"
              aria-controls="bo-sidebar"
              aria-expanded={drawerOpen}
            >
              <i className="fas fa-bars"></i>
            </button>
          )}
          <div className="bo-crumbs">
            {current && <span className="bo-crumb-group">{current.group}</span>}
            {current && <i className="fas fa-chevron-right bo-crumb-sep" aria-hidden="true"></i>}
            <span className="bo-crumb-page">{current?.name ?? 'Табло'}</span>
          </div>
        </header>

        <main className="bo-content dashboard-content">{children}</main>
      </div>

      <ConfirmModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogoutConfirm}
        title="Изход от системата"
        message="Сигурни ли сте, че искате да излезете от административния панел?"
        confirmText="Изход"
        cancelText="Отказ"
        variant="warning"
      />
    </div>
  );
};

export default Layout;
